import type { Optional } from '@festina-life/core'
import { FPlusDefaultFontWidth } from '@festina-life/fplus-monaco'
import { Card, CardBody } from '@festina-life/ui/structure'
import cx from 'classnames'
import type { CSSProperties } from 'react'

import { FPlusCodeContainer } from '../monaco/components/FPlusCodeContainer'
import type { FPlusViewerAlignment } from '../monaco/components/types'
import { DEFAULT_FPLUS_CONTAINER_PADDING } from '../monaco/components/useContainerWidthInFPlusCharacters'
import { FPlusViewer } from './FPlusViewer'
import { IndicateOverflow } from './IndicateOverflow'

/**
 * The expression wraps rather than running off the side, and is cut to the
 * height it is given. Wrapping first means a line too long to fit is cut by
 * the same edge as an expression with too many lines, so both read as one kind
 * of overflow.
 */
const clippingStyles =
  'max-h-full overflow-hidden whitespace-pre-wrap wrap-anywhere'

type Props = {
  code: string
  /**
   * The same expression formatted to the wider line length the tooltip shows
   * it at.
   */
  tooltipCode: string
  className?: string
  /**
   * Optional label written across the indicator when the expression does not
   * fit.
   */
  overflowTitle?: Optional<string>
  alignment: FPlusViewerAlignment
  /**
   * The line length the tooltip's expression was formatted to, which is what
   * the tooltip is made wide enough to show without wrapping again.
   */
  tooltipLineLength: number
}

/**
 * A read-only F-plus expression that shows itself when it fits, and gives way
 * to the overflow indicator when it does not. The expression is rendered and
 * clipped by CSS, and measured only once it is laid out, so nothing has to
 * predict how much of it will fit.
 */
export const FPlusViewerWithOverflowIndicator = ({
  code,
  tooltipCode,
  className,
  overflowTitle,
  alignment,
  tooltipLineLength,
}: Props) => (
  <IndicateOverflow<HTMLPreElement>
    title={overflowTitle}
    tip={
      <Card>
        <CardBody>
          <div
            style={
              {
                '--code-tooltip-width': `${tooltipLineLength * FPlusDefaultFontWidth + DEFAULT_FPLUS_CONTAINER_PADDING}px`,
              } as CSSProperties
            }
          >
            <FPlusCodeContainer
              className="min-w-(--code-tooltip-width) text-left"
              isTransparent={false}
            >
              {/* The expression is formatted to the width the tooltip is
                  made, but the formatter cannot break a long identifier or
                  string literal, so one that still does not fit is scrolled
                  to rather than cut off. */}
              <FPlusViewer
                code={tooltipCode}
                alignment="left"
                className="max-w-full overflow-x-auto"
              />
            </FPlusCodeContainer>
          </div>
        </CardBody>
      </Card>
    }
  >
    {(ref) => (
      <FPlusViewer
        ref={ref}
        code={code}
        alignment={alignment}
        className={cx(clippingStyles, className)}
      />
    )}
  </IndicateOverflow>
)
